// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/lib/statusBar.ts
// 🔧 PURPOSE: La barra di stato del telefono (ora, wifi, batteria) ha
//             sempre il colore della parte di pagina che le sta sotto. Qui
//             c'è l'UNICO punto che la scrive: prima erano una ventina
//             (index.html, App.tsx, le pagine, i fogli) e si pestavano i
//             piedi (bianco sopra lo splash blu, bianchi fissi all'uscita,
//             colori "di prima" rimessi fuori tempo).
//
//             Chi vince, dall'alto:
//             1. lo splash: blu (in scuro #1a1a1a) finché è sullo schermo;
//             2. le finestre aperte (login, chat, fogli di pagamento...),
//                l'ultima aperta sopra; i fogli con lo sfondo scurito
//                velano il colore di sotto invece di sostituirlo;
//             3. la riga della consegna in cima (DeliveryWidget);
//             4. il colore della pagina (useStatusBarColor);
//             5. la tabella dei percorsi qui sotto: ogni rotta di App.tsx ha
//                il suo colore, anche mentre il pezzo della pagina arriva.
//             In tema scuro vale #1a1a1a, salvo un colore scuro dato.
//
//             Ogni cambio SOSTITUISCE il meta theme-color (uno solo, nuovo,
//             primo in <head>: l'app installata su iPhone non sempre segue
//             un meta modificato sul posto) e mette lo stesso colore come
//             sfondo di <html> e <body> (Safari 26+ colora la barra da lì).
//             Gli hook scrivono in useLayoutEffect, prima che si veda; tornando
//             sull'app (visibilitychange, pageshow, focus) si riscrive.
//
//             apple-mobile-web-app-status-bar-style resta "default":
//             cambiarlo obbligherebbe gli ospiti a reinstallare l'app.
//
//             SOTTO L'OROLOGIO, LO STILE NATIVO (solo app installata su
//             iPhone e iPad). iOS 26+ ci disegna una fascia sfumata ("scroll
//             edge effect" del Liquid Glass) sui primi px della pagina,
//             sempre; non si spegne con meta né CSS. Fra le prove col
//             pannello (striscia a tinta unita di 12-44px, bordi fissi, stile
//             nativo +10/14/18px) il proprietario ha scelto "stile nativo
//             +10px": la sfumatura di iOS resta, sopra gli sfondi e le foto
//             delle testate (che partono da 0, come prima), e solo il
//             CONTENUTO scende di 10px, fuori dalla fascia. Allora, all'avvio,
//             se l'app è quella installata (navigator.standalone su iOS/iPadOS):
//             - <html> prende la classe nva-ios-standalone, che mette
//               --nva-top-edge a 10px (styles.css; altrove resta 0px) e con
//               lei --safe-area-inset-top (il contenuto delle testate) e
//               --nva-screen-top (chi copre tutto lo schermo: login, chat,
//               fogli, iframe) = zona sicura + 10px;
//             - la guardia in cima resta accesa: la barra continua a prendere
//               il colore scritto qui, pagina per pagina;
//             - niente striscia e niente margine del Layout (--nva-top-gap 0).
//             La riga della consegna (DeliveryWidget), finché c'è, azzera
//             --safe-area-inset-top per le pagine sotto di lei e i 10px li
//             aggiunge al suo contenuto.
//             Android, computer e Safari nella scheda: 0px, tutto come prima.
//             Il pannello di prova può ancora rimettere la striscia (e il
//             Layout che scende) o cambiare i px, fino alla riapertura.
// ============================================

import { useLayoutEffect, useRef } from 'react';
import { SERVICE_PATHS } from './servicePages';

// ---- I colori ----------------------------------------------------------------------

/** Lo splash di index.html. */
export const SPLASH_COLOR = '#1e3a8a';
/** Tema scuro: lo stesso fondo delle intestazioni scure (nva.night). */
export const DARK_COLOR = '#1a1a1a';
/** L'intestazione della Home (nva.espresso), e la velatura delle foto dei servizi. */
export const HOME_COLOR = '#2a2620';
/** Eventi, Ristoranti, Partner, le città (nva.sand). */
export const SAND_COLOR = '#f7f4ee';
/**
 * Esplora: il cielo in cima alla foto della testata (hero-mola.jpg, media
 * della fascia centrale delle prime righe). La striscia sopra la foto
 * continua il cielo invece di tagliarlo con il color sabbia; la foto è la
 * stessa anche in tema scuro, quindi vale per entrambi.
 */
export const EXPLORE_SKY_COLOR = '#5898e7';
export const WHITE_COLOR = '#ffffff';
/** Il fondo dell'app sotto le pagine (gray-50, Layout). */
export const APP_BG_COLOR = '#f9fafb';

// I nomi di prima (lib/themeColor): li usano ancora le pagine dei servizi.
export const HOME_THEME_COLOR = '#2A2620';
export const DARK_THEME_COLOR = DARK_COLOR;
export const LIGHT_THEME_COLOR = WHITE_COLOR;

// ---- La tabella dei percorsi -------------------------------------------------------
// Il colore in cima a ogni pagina, in chiaro. Le pagine con più aspetti
// (strumenti di Esplora, iframe, lettore del tour) lo cambiano da sole.

const ROUTE_COLORS: Record<string, string> = {
  '/': HOME_COLOR,
  '/restaurants': SAND_COLOR,
  '/partners': SAND_COLOR,
  '/events': SAND_COLOR,
  '/explore': EXPLORE_SKY_COLOR,
  // Il gradiente della testata, dal suo colore di partenza (come i servizi).
  '/my-stay': '#0ea5e9',
  '/group-check-in': '#a855f7',
  '/extras': '#f59e0b',
  // La barra bianca in cima (ExtraDetail, carrello, collegamento).
  '/extras/cart': WHITE_COLOR,
  '/claim-booking': WHITE_COLOR,
  // Il check-in senza iframe non ha un fondo suo: si vede quello dell'app.
  '/check-in': APP_BG_COLOR,
  '/book': WHITE_COLOR,
  '/gift-card': WHITE_COLOR,
  // Le tue gift card: la testata espresso (pages/MyGiftCards).
  '/my-gift-cards': '#2a2620',
  '/payment-success': '#f0fdf4',
  '/auto-login': '#eff6ff',
  '/taxi': '#fbbf24',
  '/parking': '#2563eb',
  '/pharmacy': '#10b981',
  '/laundry': '#14b8a6',
  '/travel-notes': '#7c3aed',
  '/travel-wallet': '#ea580c',
  '/offers/romantic': HOME_COLOR,
  [SERVICE_PATHS.delivery]: HOME_COLOR,
  [SERVICE_PATHS.rentCar]: HOME_COLOR,
  [SERVICE_PATHS.excursions]: HOME_COLOR,
  [SERVICE_PATHS.excursionPlanner]: '#b45309',
  [SERVICE_PATHS.massage]: '#a855f7',
  [SERVICE_PATHS.rentBike]: HOME_COLOR,
  [SERVICE_PATHS.emergency]: '#dc2626',
  [SERVICE_PATHS.recycle]: '#84cc16',
  [SERVICE_PATHS.game]: '#8b5cf6',
};

const PREFIX_COLORS: Array<[RegExp, string]> = [
  // La barra bianca in cima alle attrazioni.
  [/^\/cities\/[^/]+\/attraction\//, WHITE_COLOR],
  [/^\/cities\/[^/]+$/, SAND_COLOR],
  [/^\/extras\//, WHITE_COLOR],
  [/^\/audio-tour\//, WHITE_COLOR],
];

/** Gli strumenti a tutto schermo di Esplora (?view=...): la loro testata. */
export const EXPLORE_TOOL_COLORS: Record<string, string> = {
  map: '#3b82f6',
  maps: '#3b82f6',
  weather: '#3b82f6',
  trains: '#475569',
  planner: '#059669',
};

/** Il colore (in chiaro) di un percorso, anche prima che la pagina arrivi. */
export function statusColorForPath(pathname: string, search: string = ''): string {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') || '/' : pathname || '/';
  if (path === '/explore') {
    const view = new URLSearchParams(search).get('view');
    if (view && Object.prototype.hasOwnProperty.call(EXPLORE_TOOL_COLORS, view)) return EXPLORE_TOOL_COLORS[view];
  }
  if (Object.prototype.hasOwnProperty.call(ROUTE_COLORS, path)) return ROUTE_COLORS[path];
  for (const [pattern, color] of PREFIX_COLORS) {
    if (pattern.test(path)) return color;
  }
  return APP_BG_COLOR;
}

// ---- Il calcolo, senza stato --------------------------------------------------------

/** Un colore in chiaro e, se serve, in scuro (senza: #1a1a1a). */
export interface StatusTint {
  light: string;
  dark?: string;
}

/** Un velo sopra quello che c'è sotto (i fogli con lo sfondo scurito). */
export interface StatusVeil {
  alpha: number;
  /** Il colore del velo; di base nero. */
  tint?: string;
  darkAlpha?: number;
  darkTint?: string;
}

export type StatusOverlay = { kind: 'color'; tint: StatusTint } | { kind: 'veil'; veil: StatusVeil };

export interface StatusBarInput {
  splash: boolean;
  dark: boolean;
  pathname: string;
  search?: string;
  /** Dalla più vecchia alla più nuova. */
  overlays: StatusOverlay[];
  delivery: StatusTint | null;
  page: StatusTint | null;
}

const HEX = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

/** '#ABC' e '#AABBCC' → '#aabbcc'; quello che non è esadecimale resta com'è. */
export function normalizeColor(color: string): string {
  const match = HEX.exec(color.trim());
  if (!match) return color.trim().toLowerCase();
  const hex = match[1].length === 3 ? match[1].split('').map((c) => c + c).join('') : match[1];
  return `#${hex.toLowerCase()}`;
}

function channels(color: string): [number, number, number] | null {
  const hex = normalizeColor(color);
  if (!HEX.test(hex)) return null;
  return [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16)];
}

/** `over` con opacità `alpha` sopra `under` (in sRGB, come il browser). */
export function mixColors(under: string, over: string, alpha: number): string {
  const a = channels(under);
  const b = channels(over);
  if (!a || !b) return normalizeColor(under);
  const k = Math.min(1, Math.max(0, alpha));
  return `#${a.map((v, i) => {
    const hex = Math.round(v * (1 - k) + b[i] * k).toString(16);
    return hex.length < 2 ? `0${hex}` : hex;
  }).join('')}`;
}

const pick = (tint: StatusTint, dark: boolean): string => normalizeColor(dark ? tint.dark || DARK_COLOR : tint.light);

/** Il colore della barra per quello stato. */
export function resolveStatusColor(input: StatusBarInput): string {
  const { dark } = input;
  if (input.splash) return dark ? DARK_COLOR : SPLASH_COLOR;
  let color: string;
  if (input.delivery) color = pick(input.delivery, dark);
  else if (input.page) color = pick(input.page, dark);
  else color = dark ? DARK_COLOR : statusColorForPath(input.pathname, input.search);
  input.overlays.forEach((overlay) => {
    if (overlay.kind === 'color') {
      color = pick(overlay.tint, dark);
    } else {
      const { veil } = overlay;
      const tint = dark ? veil.darkTint ?? veil.tint ?? '#000000' : veil.tint ?? '#000000';
      color = mixColors(color, tint, dark ? veil.darkAlpha ?? veil.alpha : veil.alpha);
    }
  });
  return color;
}

// ---- Lo stato ----------------------------------------------------------------------

export interface StatusBarHandle<T> {
  update: (value: T) => void;
  remove: () => void;
}

interface Entry<T> {
  id: number;
  value: T;
}

let pages: Array<Entry<StatusTint>> = [];
let overlays: Array<Entry<StatusOverlay>> = [];
let delivery: StatusTint | null = null;
let splash = false;
let started = false;
let suspended = 0;
let committed: string | null = null;
let nextId = 1;
let reassertTimer: ReturnType<typeof setTimeout> | null = null;
let teardown: Array<() => void> = [];

/** La classe su <html> (index.html) che tiene blu la pagina durante lo splash. */
const SPLASH_CLASS = 'nva-splash';
/** Tornando sull'app si riscrive subito e ancora dopo un attimo (l'iPhone legge il colore quando vuole lui). */
const REASSERT_DELAY_MS = 300;
export const TOP_GUARD_ID = 'nva-top-guard';
/** Lo spostamento dello stile nativo sotto l'orologio (styles.css: 10px nell'app installata su iPhone, 0px altrove). */
const TOP_EDGE_VAR = '--nva-top-edge';

const hasDocument = (): boolean => typeof document !== 'undefined' && !!document.documentElement;

function prefersDark(): boolean {
  try {
    return typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch {
    return false;
  }
}

function snapshot(): StatusBarInput {
  const loc = typeof window !== 'undefined' ? window.location : null;
  return {
    splash,
    dark: prefersDark(),
    pathname: loc ? loc.pathname : '/',
    search: loc ? loc.search : '',
    overlays: overlays.map((e) => e.value),
    delivery,
    page: pages.length ? pages[pages.length - 1].value : null,
  };
}

/**
 * PROVE DAL PANNELLO (5 tocchi sul logo): senza la guardia e senza la
 * striscia, iOS 26/27 prende il colore da un elemento fisso in cima (barra
 * netta, niente sfumatura) ma lo legge una volta sola. Qui un bordo fisso
 * sottile del colore corrente: 'replace' lo toglie e ne mette uno nuovo a ogni
 * cambio (per costringere l'iPhone a rileggerlo), 'update' gli cambia solo il
 * colore (prova di controllo). Vale fino alla riapertura dell'app.
 */
export type EdgeTestMode = 'off' | 'replace-1' | 'replace-6' | 'update-1';
const EDGE_TEST_ID = 'nva-edge-test';
let edgeMode: EdgeTestMode = 'off';

function applyEdge(color: string): void {
  if (!hasDocument() || !document.body) return;
  const old = document.getElementById(EDGE_TEST_ID);
  if (edgeMode === 'off') {
    if (old && old.parentNode) old.parentNode.removeChild(old);
    return;
  }
  if (edgeMode === 'update-1' && old) {
    old.style.backgroundColor = color;
    return;
  }
  if (old && old.parentNode) old.parentNode.removeChild(old);
  const edge = document.createElement('div');
  edge.id = EDGE_TEST_ID;
  edge.setAttribute('aria-hidden', 'true');
  edge.style.cssText = [
    'position:fixed',
    'top:0',
    'left:0',
    'right:0',
    `height:${edgeMode === 'replace-6' ? 6 : 1}px`,
    `background-color:${color}`,
    'pointer-events:none',
    'z-index:2147483646',
  ].join(';');
  document.body.appendChild(edge);
}

/** Per il pannello: accende una prova (spegne guardia, striscia e i 10px) o la toglie. */
export function setEdgeTest(mode: EdgeTestMode): void {
  if (!hasDocument()) return;
  edgeMode = mode;
  if (mode !== 'off') {
    setTopGuard(false);
    setTopGap(0);
  }
  applyEdge(committed || document.documentElement.getAttribute('data-status-color') || '#2A2620');
}

/**
 * Per il pannello: lo stile nativo con un altro spostamento (niente
 * striscia, guardia accesa, il CONTENUTO delle testate e delle finestre a
 * tutto schermo `px` più in basso), anche fuori dall'app installata. null
 * toglie la prova: di nuovo come di serie (10px nell'app installata su
 * iPhone, 0px altrove). Vale fino alla riapertura dell'app.
 */
export function setNativeEdgeTest(px: number | null): void {
  if (!hasDocument()) return;
  const root = document.documentElement;
  if (px === null) {
    root.style.removeProperty(TOP_EDGE_VAR);
    return;
  }
  setEdgeTest('off');
  setTopGap(0);
  setTopGuard(true);
  root.style.setProperty(TOP_EDGE_VAR, `${Math.max(0, Math.round(px))}px`);
}

/** Un meta nuovo (e uno solo), primo in <head>; lo stesso colore sotto la pagina. */
function write(color: string): void {
  const doc = document;
  Array.prototype.slice.call(doc.querySelectorAll('meta[name="theme-color"]')).forEach((node: Element) => {
    if (node.parentNode) node.parentNode.removeChild(node);
  });
  const head = doc.head || doc.getElementsByTagName('head')[0];
  if (head) {
    const meta = doc.createElement('meta');
    meta.setAttribute('name', 'theme-color');
    meta.setAttribute('content', color);
    head.insertBefore(meta, head.firstChild);
  }
  doc.documentElement.style.backgroundColor = color;
  if (doc.body) doc.body.style.backgroundColor = color;
  doc.documentElement.setAttribute('data-status-color', color);
  // Lo stesso colore come variabile CSS, per chi deve seguirlo (la striscia delle prove).
  doc.documentElement.style.setProperty('--nva-status-color', color);
  if (edgeMode !== 'off') applyEdge(color);
}

function commit(force: boolean): void {
  if (!hasDocument() || suspended > 0) return;
  const color = resolveStatusColor(snapshot());
  if (!force && color === committed && document.querySelectorAll('meta[name="theme-color"]').length === 1) return;
  committed = color;
  write(color);
}

/** Ricalcola e scrive se il colore è cambiato (`force`: riscrive comunque). */
export function refreshStatusBar(force: boolean = false): void {
  commit(force);
}

function addEntry<T>(list: () => Array<Entry<T>>, set: (next: Array<Entry<T>>) => void, value: T): StatusBarHandle<T> {
  const id = nextId++;
  set([...list(), { id, value }]);
  commit(false);
  return {
    update: (next: T) => {
      set(list().map((e) => (e.id === id ? { id, value: next } : e)));
      commit(false);
    },
    remove: () => {
      set(list().filter((e) => e.id !== id));
      commit(false);
    },
  };
}

/** Il colore di una pagina (l'ultima registrata vince). */
export function addPageColor(tint: StatusTint): StatusBarHandle<StatusTint> {
  return addEntry(() => pages, (next) => { pages = next; }, tint);
}

/** Una finestra aperta sopra la pagina (l'ultima aperta vince). */
export function addStatusOverlay(overlay: StatusOverlay): StatusBarHandle<StatusOverlay> {
  return addEntry(() => overlays, (next) => { overlays = next; }, overlay);
}

/** La riga della consegna in cima (null: non c'è). */
export function setDeliveryBarColor(tint: StatusTint | null): void {
  delivery = tint;
  commit(false);
}

/** Lo splash è ancora sullo schermo? */
export function isStatusBarSplash(): boolean {
  return splash;
}

/** Lo splash è sparito: da qui vale la pagina. */
export function endStatusBarSplash(): void {
  splash = false;
  if (hasDocument()) document.documentElement.classList.remove(SPLASH_CLASS);
  commit(false);
}

/**
 * Il pannello di prova (StatusBarProbe) scrive da solo: finché è aperto qui
 * non si scrive niente. Restituisce la funzione che riprende (e riscrive).
 */
export function suspendStatusBar(): () => void {
  suspended += 1;
  let done = false;
  return () => {
    if (done) return;
    done = true;
    suspended = Math.max(0, suspended - 1);
    commit(true);
  };
}

/**
 * Safari 26+ (nella scheda) colora la barra dall'elemento fisso che trova in
 * cima; il Layout è fisso e grande quanto lo schermo, e un elemento così
 * tiene il PRIMO colore visto (WebKit, LocalFrameView::fixedContainerEdges:
 * "preferExistingColor"): restava il blu dello splash. Questo quadratino
 * invisibile in cima al centro è troppo piccolo per essere preso lui e
 * copre tutto il resto: niente elemento fisso in cima, e Safari usa lo
 * sfondo della pagina, cioè il colore scritto qui. Non riceve tocchi.
 */
function ensureTopGuard(): void {
  if (!hasDocument() || !document.body || document.getElementById(TOP_GUARD_ID)) return;
  const guard = document.createElement('div');
  guard.id = TOP_GUARD_ID;
  guard.setAttribute('aria-hidden', 'true');
  guard.style.cssText = [
    'position:fixed',
    'top:0',
    'left:30%',
    'width:40%',
    'height:8px',
    'pointer-events:none',
    'z-index:2147483647',
    // Uno sfondo (trasparente) e un figlio: per WebKit non è "vuoto".
    'background-image:linear-gradient(transparent,transparent)',
  ].join(';');
  guard.appendChild(document.createElement('i'));
  document.body.appendChild(guard);
}

/** Per il pannello di prova: la guardia in cima accesa o spenta. */
export function setTopGuard(on: boolean): void {
  if (!hasDocument()) return;
  if (on) ensureTopGuard();
  else {
    const guard = document.getElementById(TOP_GUARD_ID);
    if (guard && guard.parentNode) guard.parentNode.removeChild(guard);
  }
}

// ---- Sotto l'orologio (app installata su iPhone/iPad) --------------------------------

export const TOP_STRIP_ID = 'nva-top-strip';
/** La classe su <html> dell'app installata su iPhone/iPad: --nva-top-edge a 10px (styles.css). */
export const IOS_STANDALONE_CLASS = 'nva-ios-standalone';
/** Di quanto scende il contenuto delle testate nell'app installata (la scelta del proprietario, la stessa di styles.css). */
export const TOP_EDGE_PX = 10;

/** L'app aperta dall'icona sulla schermata Home di un iPhone o iPad (non Safari, non Android). */
export function isIosStandalone(): boolean {
  try {
    if (typeof navigator === 'undefined') return false;
    const nav = navigator as Navigator & { standalone?: boolean };
    if (nav.standalone !== true) return false;
    const ua = nav.userAgent || '';
    // iPadOS si presenta come un Mac: lo tradisce lo schermo tattile.
    return /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && (nav.maxTouchPoints || 0) > 1);
  } catch {
    return false;
  }
}

/**
 * La striscia a tinta unita sotto l'orologio (solo prove dal pannello): copre
 * la fascia sfumata di iOS con il colore della barra di stato (quello scritto
 * qui, finestre comprese). Sopra le pagine e le finestre, sotto la guardia;
 * non riceve tocchi e non si legge. Alta var(--nva-top-gap): quanto scendono
 * le pagine.
 */
function ensureTopStrip(): void {
  if (!hasDocument() || !document.body || document.getElementById(TOP_STRIP_ID)) return;
  const strip = document.createElement('div');
  strip.id = TOP_STRIP_ID;
  strip.setAttribute('aria-hidden', 'true');
  strip.setAttribute('style', [
    'position:fixed',
    'top:0',
    'left:0',
    'right:0',
    'height:var(--nva-top-gap,0px)',
    'background-color:var(--nva-status-color,transparent)',
    'pointer-events:none',
    'z-index:2147483000',
  ].join(';'));
  document.body.appendChild(strip);
}

function removeTopStrip(): void {
  const strip = hasDocument() ? document.getElementById(TOP_STRIP_ID) : null;
  if (strip && strip.parentNode) strip.parentNode.removeChild(strip);
}

/**
 * Per il pannello di prova: la striscia a tinta unita alta `px` (il Layout
 * scende di altrettanto e i 10px dello stile nativo si spengono: la fascia
 * la copre lei), anche fuori dall'app installata; 0 toglie striscia e
 * spostamento (la pagina nuda sotto la sfumatura); null rimette come di
 * serie (stile nativo, niente striscia). Vale fino alla riapertura dell'app.
 */
export function setTopGap(px: number | null): void {
  if (!hasDocument()) return;
  const root = document.documentElement;
  if (px === null) {
    root.style.removeProperty('--nva-top-gap');
    root.style.removeProperty(TOP_EDGE_VAR);
    removeTopStrip();
    return;
  }
  const height = Math.max(0, Math.round(px));
  root.style.setProperty('--nva-top-gap', `${height}px`);
  root.style.setProperty(TOP_EDGE_VAR, '0px');
  if (height > 0) ensureTopStrip();
  else removeTopStrip();
}

function reassert(): void {
  commit(true);
  if (reassertTimer) clearTimeout(reassertTimer);
  reassertTimer = setTimeout(() => {
    reassertTimer = null;
    commit(true);
  }, REASSERT_DELAY_MS);
}

/**
 * All'avvio (index.tsx, prima di React): lo splash se c'è, il primo colore,
 * la guardia in cima, la classe dello stile nativo (solo app installata su
 * iPhone/iPad: contenuti 10px più in basso) e gli ascoltatori. Una volta sola.
 */
export function startStatusBar(): void {
  if (started || !hasDocument()) return;
  started = true;
  splash = document.documentElement.classList.contains(SPLASH_CLASS);
  // La classe prima del primo disegno di React: le pagine nascono già al loro posto.
  if (isIosStandalone()) document.documentElement.classList.add(IOS_STANDALONE_CLASS);
  if (document.body) ensureTopGuard();
  else document.addEventListener('DOMContentLoaded', ensureTopGuard);

  const onVisible = () => {
    if (document.visibilityState === 'visible') reassert();
  };
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('pageshow', reassert);
  window.addEventListener('focus', reassert);
  teardown.push(() => {
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('pageshow', reassert);
    window.removeEventListener('focus', reassert);
  });

  const media = typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-color-scheme: dark)') : null;
  if (media) {
    const onScheme = () => commit(false);
    // Safari fino alla 13 conosce solo addListener.
    if (typeof media.addEventListener === 'function') {
      media.addEventListener('change', onScheme);
      teardown.push(() => media.removeEventListener('change', onScheme));
    } else if (typeof media.addListener === 'function') {
      media.addListener(onScheme);
      teardown.push(() => media.removeListener(onScheme));
    }
  }

  commit(true);
}

/** Solo per i test: tutto come all'apertura. */
export function resetStatusBarForTests(): void {
  teardown.forEach((fn) => fn());
  teardown = [];
  if (reassertTimer) clearTimeout(reassertTimer);
  reassertTimer = null;
  pages = [];
  overlays = [];
  delivery = null;
  splash = false;
  started = false;
  suspended = 0;
  committed = null;
  nextId = 1;
  if (hasDocument()) {
    removeTopStrip();
    document.documentElement.classList.remove(IOS_STANDALONE_CLASS);
    document.documentElement.style.removeProperty('--nva-top-gap');
    document.documentElement.style.removeProperty(TOP_EDGE_VAR);
  }
}

// ---- Gli hook ----------------------------------------------------------------------

/**
 * Il colore della pagina, finché è aperta (`dark`: il colore in tema scuro,
 * di base #1a1a1a). Un colore che cambia resta al suo posto.
 */
export function useStatusBarColor(color: string, options: { dark?: string; enabled?: boolean } = {}): void {
  const { dark, enabled = true } = options;
  const latest = useRef<StatusTint>({ light: color, dark });
  latest.current = { light: color, dark };
  const handle = useRef<StatusBarHandle<StatusTint> | null>(null);

  useLayoutEffect(() => {
    if (!enabled) return undefined;
    const h = addPageColor(latest.current);
    handle.current = h;
    return () => {
      h.remove();
      if (handle.current === h) handle.current = null;
    };
  }, [enabled]);

  useLayoutEffect(() => {
    handle.current?.update({ light: color, dark });
  }, [color, dark]);
}

function useOverlay(overlay: StatusOverlay, open: boolean, key: string): void {
  const latest = useRef(overlay);
  latest.current = overlay;
  const handle = useRef<StatusBarHandle<StatusOverlay> | null>(null);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const h = addStatusOverlay(latest.current);
    handle.current = h;
    return () => {
      h.remove();
      if (handle.current === h) handle.current = null;
    };
  }, [open]);

  useLayoutEffect(() => {
    handle.current?.update(latest.current);
  }, [key]);
}

/** Una finestra a tutto schermo con il suo colore in cima, finché `open`. */
export function useStatusBarOverlay(color: string, open: boolean, options: { dark?: string } = {}): void {
  useOverlay({ kind: 'color', tint: { light: color, dark: options.dark } }, open, `${color}|${options.dark || ''}`);
}

/** Un foglio con lo sfondo scurito (es. bg-black/50): la barra si scurisce come la pagina. */
export function useStatusBarVeil(alpha: number, open: boolean, options: Omit<StatusVeil, 'alpha'> = {}): void {
  const veil: StatusVeil = { alpha, ...options };
  useOverlay({ kind: 'veil', veil }, open, `${alpha}|${options.tint || ''}|${options.darkAlpha ?? ''}|${options.darkTint || ''}`);
}

/** La riga della consegna (DeliveryWidget): il suo fondo, o null quando non c'è. */
export function useStatusBarDeliveryBar(tint: StatusTint | null): void {
  const light = tint ? tint.light : null;
  const dark = tint ? tint.dark : undefined;
  useLayoutEffect(() => {
    if (!light) return undefined;
    setDeliveryBarColor({ light, dark });
    return () => setDeliveryBarColor(null);
  }, [light, dark]);
}

/** Cambio di percorso (Layout): la tabella vale per quello nuovo, prima che si veda. */
export function useStatusBarRoute(pathname: string, search: string): void {
  useLayoutEffect(() => {
    commit(false);
  }, [pathname, search]);
}

/** Il nome di prima (hooks/useThemeColor): il colore della pagina in chiaro. */
export function useThemeColor(color: string, enabled: boolean = true): void {
  useStatusBarColor(color, { enabled });
}
